'use client';

import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { createChart, CandlestickSeries, LineSeries, type IChartApi, type ISeriesApi, type Time } from 'lightweight-charts';
import { useLang } from '@/lib/i18n/LanguageContext';

// TradingView-style VISUAL signal input. A real candlestick chart of the instrument with a draggable
// long/short position tool (entry / stop / target). The user drags the levels on the chart; on publish
// we screenshot THIS chart so the stored image is pixel-identical to what they drew — no server-side
// reconstruction. Values are also editable numerically for precision.

export interface PositionLevels { entry: number; sl: number; tp: number }
export interface PositionChartHandle {
  getLevels: () => PositionLevels;
  /** Composite screenshot (candles + position overlay + R:R + watermark) as a PNG blob. */
  capture: () => Promise<Blob | null>;
  hasCandles: () => boolean;
}

interface Props {
  market: string;
  ticker: string;
  timeframe: string;
  direction: 'BUY' | 'SELL';
  levels: PositionLevels | null;
  onLevels: (l: PositionLevels) => void;
  onTimeframe?: (tf: string) => void;
  height?: number | string;
}

// Timeframe buttons shown in the chart toolbar (TradingView-style quick switch).
const TF_BUTTONS = ['5min', '15min', '30min', '1h', '4h', '1day'] as const;
const TF_LABEL: Record<string, string> = { M1: '1m', M5: '5m', M15: '15m', M30: '30m', H1: '1H', H4: '4H', D1: '1D', '5min': '5m', '15min': '15m', '30min': '30m', '1h': '1H', '4h': '4H', '1day': '1D' };
// The composer's timeframe values are M5/M15/H1/… — map the toolbar's onTimeframe back to those.
const TF_TO_COMPOSER: Record<string, string> = { '5min': 'M5', '15min': 'M15', '30min': 'M30', '1h': 'H1', '4h': 'H4', '1day': 'D1' };
const COMPOSER_TO_TF: Record<string, string> = { M1: '5min', M5: '5min', M15: '15min', M30: '30min', H1: '1h', H4: '4h', D1: '1day' };

type Candle = { time: number; open: number; high: number; low: number; close: number };

const IV_BINANCE: Record<string, string> = { M1: '1m', M5: '5m', M15: '15m', M30: '30m', H1: '1h', H4: '4h', D1: '1d' };

async function fetchCandles(market: string, ticker: string, tf: string): Promise<Candle[]> {
  const up = String(market).toUpperCase();
  const base = String(ticker).toUpperCase().replace(/[^A-Z0-9]/g, '').replace(/(USDT|USDC|USD|PERP)$/g, '');
  if (up === 'CRYPTO' && base) {
    try {
      const iv = IV_BINANCE[tf] || '1h';
      // data-api.binance.vision = Binance's public mirror without the US geo-block (451).
      for (const host of ['https://data-api.binance.vision', 'https://api.binance.com']) {
        const r = await fetch(`${host}/api/v3/klines?symbol=${base}USDT&interval=${iv}&limit=200`).catch(() => null);
        if (!r?.ok) continue;
        const rows = await r.json();
        if (Array.isArray(rows) && rows.length) {
          return rows.map((k: any[]) => ({ time: Math.floor(k[0] / 1000), open: +k[1], high: +k[2], low: +k[3], close: +k[4] }));
        }
      }
    } catch { /* fall through */ }
  }
  // Non-crypto (and crypto fallback) → server route. Needs TWELVEDATA_API_KEY on the deploy for FX/etc.
  try {
    const r = await fetch(`/api/candles?market=${encodeURIComponent(market)}&ticker=${encodeURIComponent(ticker)}&interval=${encodeURIComponent(tf)}`);
    if (r.ok) {
      const j = await r.json();
      if (Array.isArray(j?.candles) && j.candles.length) {
        return j.candles.map((c: any) => ({ time: c.time, open: c.open, high: c.high, low: c.low, close: c.close }));
      }
    }
  } catch { /* no data */ }
  return [];
}

function fmtPrice(p: number): string {
  if (!Number.isFinite(p)) return '—';
  if (p >= 10000) return p.toFixed(0);
  if (p >= 100) return p.toFixed(2);
  if (p >= 1) return p.toFixed(4);
  return p.toFixed(5);
}

// Owner rule: risk box RED, reward box BLUE (MT-style), entry amber.
const COL = { entry: '#f59e0b', tp: '#3b82f6', sl: '#ef4444' };
const ZONE = { tp: 'rgba(59,130,246,0.13)', sl: 'rgba(239,68,68,0.12)' };

const PositionChart = forwardRef<PositionChartHandle, Props>(function PositionChart(
  { market, ticker, timeframe, direction, levels, onLevels, onTimeframe, height = 380 },
  ref,
) {
  const { t } = useLang();
  const wrapRef = useRef<HTMLDivElement>(null);
  const chartElRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<IChartApi | null>(null);
  const seriesRef = useRef<ISeriesApi<'Candlestick'> | null>(null);
  const levelSeriesRef = useRef<ISeriesApi<'Line'> | null>(null);
  const [loading, setLoading] = useState(true);
  const [noData, setNoData] = useState(false);
  const [candles, setCandles] = useState<Candle[]>([]);
  const [ohlc, setOhlc] = useState<Candle | null>(null); // hovered (crosshair) or last candle, for the header
  const activeTf = COMPOSER_TO_TF[timeframe] || '1h';
  // Pixel y for each level, synced from the chart each frame so the overlay tracks pan/zoom/rescale.
  const [coords, setCoords] = useState<{ entry: number; sl: number; tp: number } | null>(null);
  const isBuy = direction === 'BUY';
  // Optimistic local levels drive the drag so it stays glass-smooth regardless of how heavy the
  // parent (post modal) is to re-render. Synced FROM the parent only when not dragging (e.g. typing).
  const [localLevels, setLocalLevels] = useState<PositionLevels | null>(levels);
  const levelsRef = useRef(localLevels);
  levelsRef.current = localLevels;
  const dragRef = useRef<null | 'entry' | 'sl' | 'tp'>(null);
  useEffect(() => { if (!dragRef.current) setLocalLevels(levels); }, [levels]);

  // Flip direction → mirror TP/SL across entry (a BUY's TP-above/SL-below becomes a SELL's
  // TP-below/SL-above), keeping the same reward/risk distances. Otherwise the box stays upside-down.
  const prevBuyRef = useRef(isBuy);
  useEffect(() => {
    if (prevBuyRef.current === isBuy) return;
    prevBuyRef.current = isBuy;
    const l = levelsRef.current;
    if (!l) return;
    const reward = Math.abs(l.tp - l.entry), risk = Math.abs(l.sl - l.entry);
    const flipped = { entry: l.entry, tp: isBuy ? l.entry + reward : l.entry - reward, sl: isBuy ? l.entry - risk : l.entry + risk };
    setLocalLevels(flipped);
    onLevels(flipped);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isBuy]);

  // ── load candles + build chart ──────────────────────────────────────────
  useEffect(() => {
    let disposed = false;
    setLoading(true); setNoData(false);
    fetchCandles(market, ticker, timeframe).then((cs) => {
      if (disposed) return;
      setCandles(cs);
      setLoading(false);
      if (!cs.length) { setNoData(true); return; }
      // Seed default levels off the last close if none set yet.
      if (!levelsRef.current) {
        const last = cs[cs.length - 1].close;
        const span = Math.max(...cs.map((c) => c.high)) - Math.min(...cs.map((c) => c.low)) || last * 0.01;
        const entry = last;
        const tp = isBuy ? entry + span * 0.5 : entry - span * 0.5;
        const sl = isBuy ? entry - span * 0.3 : entry + span * 0.3;
        const seeded = { entry, sl, tp };
        setLocalLevels(seeded);
        onLevels(seeded);
      }
    });
    return () => { disposed = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [market, ticker, timeframe]);

  // Build/replace the lightweight-charts instance when candles arrive.
  useEffect(() => {
    const el = chartElRef.current;
    if (!el || !candles.length) return;
    if (chartRef.current) { chartRef.current.remove(); chartRef.current = null; seriesRef.current = null; }
    const chart = createChart(el, {
      autoSize: true,
      layout: { background: { color: '#ffffff' } as any, textColor: '#374151', fontSize: 11 },
      grid: { vertLines: { color: 'rgba(42,46,57,0.06)' }, horzLines: { color: 'rgba(42,46,57,0.06)' } },
      timeScale: { timeVisible: timeframe !== 'D1', secondsVisible: false, borderColor: '#e5e7eb', rightOffset: 6 },
      rightPriceScale: { borderColor: '#e5e7eb' },
      crosshair: { mode: 0 },
    });
    const series = chart.addSeries(CandlestickSeries, {
      upColor: '#26a69a', downColor: '#ef5350', borderVisible: false,
      wickUpColor: '#26a69a', wickDownColor: '#ef5350',
    });
    series.setData(candles.map((c) => ({ time: c.time as Time, open: c.open, high: c.high, low: c.low, close: c.close })) as any);
    // OHLC header: last candle by default, the hovered candle while the crosshair is on the chart.
    setOhlc(candles[candles.length - 1] || null);
    chart.subscribeCrosshairMove((param) => {
      const d: any = param.seriesData.get(series);
      if (d && typeof d.open === 'number') setOhlc({ time: Number(param.time) || 0, open: d.open, high: d.high, low: d.low, close: d.close });
      else setOhlc(candles[candles.length - 1] || null);
    });
    // Invisible series carrying the level prices, so the price scale auto-fits to include entry/SL/TP
    // (not just the candles) — the user can always see and drag every level even when TP/SL sit beyond
    // the visible candle range. Its data is refreshed whenever the levels change.
    const levelSeries = chart.addSeries(LineSeries, {
      color: 'rgba(0,0,0,0)', lineWidth: 1, lastValueVisible: false, priceLineVisible: false, crosshairMarkerVisible: false,
    } as any);
    levelSeriesRef.current = levelSeries;
    chartRef.current = chart;
    seriesRef.current = series;
    // autoSize measures width async; fitContent applied while the container is 0-wide bunches the
    // candles to the right. Defer to the next frames (matches LiveTradeChart).
    const fit = () => { try { chart.timeScale().fitContent(); } catch { /* removed */ } };
    requestAnimationFrame(() => { fit(); requestAnimationFrame(fit); });
    setTimeout(fit, 140);
    return () => { chart.remove(); chartRef.current = null; seriesRef.current = null; };
  }, [candles, timeframe]);

  // Refresh the invisible level series whenever levels change → the price scale keeps entry/SL/TP in view.
  useEffect(() => {
    const ls = levelSeriesRef.current;
    if (!ls || !localLevels || candles.length < 2) return;
    const t0 = candles[0].time, t1 = candles[Math.floor(candles.length / 2)].time, t2 = candles[candles.length - 1].time;
    ls.setData([
      { time: t0 as Time, value: localLevels.tp },
      { time: t1 as Time, value: localLevels.entry },
      { time: t2 as Time, value: localLevels.sl },
    ] as any);
  }, [localLevels, candles]);

  // Keep the overlay handles pinned to the price scale every frame (tracks autoscale/pan/zoom).
  useEffect(() => {
    let raf = 0;
    const tick = () => {
      const s = seriesRef.current; const l = levelsRef.current;
      if (s && l) {
        const e = s.priceToCoordinate(l.entry);
        const sl = s.priceToCoordinate(l.sl);
        const tp = s.priceToCoordinate(l.tp);
        if (e != null && sl != null && tp != null) setCoords({ entry: e, sl, tp });
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, []);

  // ── dragging a level ──────────────────────────────────────────────────────
  // Window-level listeners (not pointer-capture + bubbling) so a drag reliably tracks the cursor
  // anywhere, and never falls through to browser text selection.
  const onPointerDown = (which: 'entry' | 'sl' | 'tp') => (e: React.PointerEvent) => {
    e.preventDefault();
    dragRef.current = which;
    const move = (ev: PointerEvent) => {
      const s = seriesRef.current; const el = chartElRef.current; const l = levelsRef.current;
      if (!dragRef.current || !s || !el || !l) return;
      const p = s.coordinateToPrice(ev.clientY - el.getBoundingClientRect().top);
      if (p == null || !Number.isFinite(p)) return;
      const price = p as number;
      const eps = Math.max(Math.abs(l.entry) * 1e-5, 1e-6);
      const next = { ...l };
      if (which === 'entry') {
        next.entry = price;
        if (isBuy) { next.tp = Math.max(next.tp, price + eps); next.sl = Math.min(next.sl, price - eps); }
        else { next.tp = Math.min(next.tp, price - eps); next.sl = Math.max(next.sl, price + eps); }
      } else if (which === 'tp') {
        next.tp = isBuy ? Math.max(price, l.entry + eps) : Math.min(price, l.entry - eps);
      } else {
        next.sl = isBuy ? Math.min(price, l.entry - eps) : Math.max(price, l.entry + eps);
      }
      setLocalLevels(next); // drives the visual immediately (smooth)
      onLevels(next);       // syncs the number inputs in the parent
    };
    const up = () => { dragRef.current = null; window.removeEventListener('pointermove', move); window.removeEventListener('pointerup', up); };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  };

  const rr = (() => {
    if (!localLevels) return 0;
    const risk = Math.abs(localLevels.entry - localLevels.sl), reward = Math.abs(localLevels.tp - localLevels.entry);
    return risk > 0 ? reward / risk : 0;
  })();
  const rrColor = rr >= 2 ? '#16a34a' : rr >= 1 ? '#e8940c' : '#dc2626';

  // ── imperative API ────────────────────────────────────────────────────────
  useImperativeHandle(ref, () => ({
    getLevels: () => levelsRef.current || { entry: 0, sl: 0, tp: 0 },
    hasCandles: () => candles.length > 0,
    capture: async () => {
      const chart = chartRef.current; const s = seriesRef.current; const l = levelsRef.current;
      if (!chart || !s || !l) return null;
      const shot = chart.takeScreenshot(); // candles + axes, on white
      const W = shot.width, H = shot.height;
      const out = document.createElement('canvas');
      out.width = W; out.height = H;
      const ctx = out.getContext('2d');
      if (!ctx) return null;
      ctx.drawImage(shot, 0, 0);
      const yOf = (p: number) => s.priceToCoordinate(p);
      const dpr = W / (chartElRef.current?.clientWidth || W);
      const yE = yOf(l.entry), ySl = yOf(l.sl), yTp = yOf(l.tp);
      const rightGutter = 62 * dpr; // approx price axis width so lines stop before the labels
      const plotW = W - rightGutter;
      if (yE != null && ySl != null && yTp != null) {
        const ye = yE * dpr, ys = ySl * dpr, yt = yTp * dpr;
        // zones
        ctx.fillStyle = ZONE.tp;
        ctx.fillRect(0, Math.min(ye, yt), plotW, Math.abs(yt - ye));
        ctx.fillStyle = ZONE.sl;
        ctx.fillRect(0, Math.min(ye, ys), plotW, Math.abs(ys - ye));
        // lines + labels
        const drawLine = (y: number, color: string, dashed: boolean, label: string) => {
          ctx.save();
          ctx.strokeStyle = color; ctx.lineWidth = 1.5 * dpr;
          if (dashed) ctx.setLineDash([6 * dpr, 4 * dpr]);
          ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(plotW, y); ctx.stroke();
          ctx.restore();
          ctx.fillStyle = color; ctx.font = `${Math.round(11 * dpr)}px 'Instrument Sans', sans-serif`; ctx.textBaseline = 'bottom';
          ctx.fillText(`${label} ${fmtPrice(label === 'ENTRY' ? l.entry : label === 'TP' ? l.tp : l.sl)}`, 6 * dpr, y - 3 * dpr);
        };
        drawLine(yt, COL.tp, true, 'TP');
        drawLine(ye, COL.entry, false, 'ENTRY');
        drawLine(ys, COL.sl, true, 'SL');
      }
      // R:R badge
      const pad = 8 * dpr;
      ctx.font = `${Math.round(12 * dpr)}px 'Instrument Sans', sans-serif`;
      const rrText = `R:R  1:${rr.toFixed(2)}`;
      const tw = ctx.measureText(rrText).width;
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(W - rightGutter - tw - pad * 2 - 8 * dpr, 8 * dpr, tw + pad * 2, 22 * dpr);
      ctx.strokeStyle = rrColor; ctx.lineWidth = 1 * dpr;
      ctx.strokeRect(W - rightGutter - tw - pad * 2 - 8 * dpr, 8 * dpr, tw + pad * 2, 22 * dpr);
      ctx.fillStyle = rrColor; ctx.textBaseline = 'middle';
      ctx.fillText(rrText, W - rightGutter - tw - pad - 8 * dpr, 8 * dpr + 11 * dpr);
      // watermark + ticker
      ctx.fillStyle = 'rgba(148,163,184,0.9)'; ctx.font = `${Math.round(11 * dpr)}px 'Instrument Sans', sans-serif`; ctx.textBaseline = 'top';
      ctx.fillText('showmetrade.com', W - rightGutter - 118 * dpr, H - 20 * dpr);
      ctx.fillStyle = '#1f2937'; ctx.font = `bold ${Math.round(15 * dpr)}px 'Instrument Sans', sans-serif`;
      ctx.fillText(`${String(ticker).toUpperCase()}  ${isBuy ? 'LONG' : 'SHORT'}`, 8 * dpr, 8 * dpr);
      // The feed renders images in a fixed 16:9 box with object-cover; a near-square capture would
      // get its top (and the ticker label) cropped. Pad the composite onto a 16:9 canvas on the same
      // white chart background so the feed fits it with zero crop and the corner label stays visible.
      const AR = 16 / 9;
      let fw = W, fh = H;
      if (W / H < AR) fw = Math.round(H * AR); // taller than 16:9 → add side margins
      else fh = Math.round(W / AR);            // wider than 16:9 → add top/bottom margins
      const offX = Math.round((fw - W) / 2), offY = Math.round((fh - H) / 2);
      const final = document.createElement('canvas');
      final.width = fw; final.height = fh;
      const fctx = final.getContext('2d');
      if (!fctx) return await new Promise<Blob | null>((res) => out.toBlob((b) => res(b), 'image/png'));
      fctx.fillStyle = '#ffffff'; fctx.fillRect(0, 0, fw, fh);
      fctx.drawImage(out, offX, offY);
      return await new Promise<Blob | null>((res) => final.toBlob((b) => res(b), 'image/png'));
    },
  }), [candles.length, rr, rrColor, isBuy, ticker]);

  // One level = the line + a left tag + a full-width grab strip + a draggable price pill on the right
  // axis (the cTrader-style handle). Grab the line anywhere or the pill; the value follows the cursor.
  const levelRow = (which: 'entry' | 'sl' | 'tp', y: number, color: string, label: string, price: number) => (
    <>
      <div style={{ position: 'absolute', left: 0, right: 58, top: y, height: 0, borderTop: `1.5px ${which === 'entry' ? 'solid' : 'dashed'} ${color}`, pointerEvents: 'none' }} />
      <div style={{ position: 'absolute', left: 6, top: y - 16, fontSize: 11, fontWeight: 700, color, pointerEvents: 'none' }}>{label}</div>
      <div onPointerDown={onPointerDown(which)}
        style={{ position: 'absolute', left: 0, right: 58, top: y - 11, height: 22, cursor: 'ns-resize', touchAction: 'none', pointerEvents: 'auto' }} />
      <div onPointerDown={onPointerDown(which)} title={`${t('Drag to move')} ${label}`}
        style={{ position: 'absolute', right: 1, top: y - 11, height: 22, minWidth: 56, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 6px', borderRadius: 5, background: color, color: '#fff', fontSize: 11, fontWeight: 800, fontFamily: 'monospace', cursor: 'ns-resize', touchAction: 'none', pointerEvents: 'auto', boxShadow: '0 1px 4px rgba(0,0,0,0.3)', whiteSpace: 'nowrap' }}>
        {fmtPrice(price)}
      </div>
    </>
  );

  return (
    <div ref={wrapRef} style={{ height, display: 'flex', flexDirection: 'column', borderRadius: 10, overflow: 'hidden', border: '1px solid #e5e7eb', background: '#fff', userSelect: 'none', WebkitUserSelect: 'none', touchAction: 'none' }}>
      {/* Toolbar — symbol + timeframe buttons (TradingView-style) */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '5px 10px', borderBottom: '1px solid #eef1f4', background: '#fbfcfd', flexShrink: 0 }}>
        <span style={{ fontSize: 13, fontWeight: 800, color: '#1f2937', fontFamily: 'monospace' }}>{String(ticker).toUpperCase() || '—'}</span>
        <div style={{ display: 'flex', gap: 1 }}>
          {TF_BUTTONS.map((tf) => {
            const active = tf === activeTf;
            return (
              <button key={tf} type="button" onClick={() => onTimeframe?.(TF_TO_COMPOSER[tf])}
                style={{ padding: '3px 9px', borderRadius: 5, border: 'none', cursor: 'pointer', fontSize: 11, fontWeight: 700, background: active ? 'rgba(79,70,229,0.12)' : 'transparent', color: active ? '#4f46e5' : '#8b95a5' }}>
                {TF_LABEL[tf]}
              </button>
            );
          })}
        </div>
      </div>

      {/* Chart area (chart + overlay share this box, so priceToCoordinate lines up) */}
      <div style={{ position: 'relative', flex: 1, minHeight: 0 }}>
        <div ref={chartElRef} style={{ position: 'absolute', inset: 0 }} />
        {ohlc && !noData && (
          <div style={{ position: 'absolute', top: 6, left: 8, zIndex: 4, fontSize: 11, fontFamily: 'monospace', color: '#94a3b8', pointerEvents: 'none', display: 'flex', gap: 9 }}>
            <span>O <b style={{ color: '#475569' }}>{fmtPrice(ohlc.open)}</b></span>
            <span>H <b style={{ color: '#475569' }}>{fmtPrice(ohlc.high)}</b></span>
            <span>L <b style={{ color: '#475569' }}>{fmtPrice(ohlc.low)}</b></span>
            <span>C <b style={{ color: ohlc.close >= ohlc.open ? '#16a34a' : '#dc2626' }}>{fmtPrice(ohlc.close)}</b></span>
          </div>
        )}
        {coords && !noData && (
          <div style={{ position: 'absolute', inset: 0, zIndex: 3, pointerEvents: 'none' }}>
            <div style={{ position: 'absolute', left: 0, right: 58, top: Math.min(coords.entry, coords.tp), height: Math.abs(coords.tp - coords.entry), background: ZONE.tp }} />
            <div style={{ position: 'absolute', left: 0, right: 58, top: Math.min(coords.entry, coords.sl), height: Math.abs(coords.sl - coords.entry), background: ZONE.sl }} />
            {localLevels && levelRow('tp', coords.tp, COL.tp, 'TP', localLevels.tp)}
            {localLevels && levelRow('entry', coords.entry, COL.entry, 'ENTRY', localLevels.entry)}
            {localLevels && levelRow('sl', coords.sl, COL.sl, 'SL', localLevels.sl)}
            <div style={{ position: 'absolute', top: 8, right: 66, padding: '4px 10px', borderRadius: 6, background: '#fff', border: `1px solid ${rrColor}`, color: rrColor, fontSize: 12, fontWeight: 800, fontFamily: 'monospace' }}>R:R 1:{rr.toFixed(2)}</div>
          </div>
        )}
        {loading && <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94a3b8', fontSize: 13 }}>{t('Loading chart…')}</div>}
        {/* No candles (phone without a market-data key, unknown symbol…): still SHOW the position —
            a levels-only preview drawn straight from the typed entry/SL/TP on a linear price scale,
            red risk box + blue reward box, exactly what the server fallback image will look like.
            Read-only (no candles to drag against); the number inputs below drive it. */}
        {noData && !loading && (
          localLevels ? (() => {
            const l = localLevels;
            const vals = [l.entry, l.sl, l.tp];
            let pMin = Math.min(...vals), pMax = Math.max(...vals);
            const pad = (pMax - pMin) * 0.18 || Math.abs(pMax) * 0.02 || 1;
            pMin -= pad; pMax += pad;
            const range = pMax - pMin || 1;
            const yPct = (v: number) => (1 - (v - pMin) / range) * 100;
            const box = (a: number, b: number, bg: string, key: string) => (
              <div key={key} style={{ position: 'absolute', left: 0, right: 58, top: `${Math.min(yPct(a), yPct(b))}%`, height: `${Math.abs(yPct(a) - yPct(b))}%`, background: bg }} />
            );
            const line = (v: number, color: string, dashed: boolean, label: string) => (
              <div key={label} style={{ position: 'absolute', left: 0, right: 0, top: `${yPct(v)}%`, height: 0 }}>
                <div style={{ position: 'absolute', left: 0, right: 58, top: 0, borderTop: `1.5px ${dashed ? 'dashed' : 'solid'} ${color}` }} />
                <div style={{ position: 'absolute', left: 6, top: -16, fontSize: 11, fontWeight: 700, color }}>{label}</div>
                <div style={{ position: 'absolute', right: 1, top: -11, height: 22, minWidth: 56, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 6px', borderRadius: 5, background: color, color: '#fff', fontSize: 11, fontWeight: 800, fontFamily: 'monospace', whiteSpace: 'nowrap', boxShadow: '0 1px 4px rgba(0,0,0,0.3)' }}>{fmtPrice(v)}</div>
              </div>
            );
            return (
              <div style={{ position: 'absolute', inset: 0, zIndex: 3, pointerEvents: 'none' }}>
                {box(l.entry, l.tp, ZONE.tp, 'ztp')}
                {box(l.entry, l.sl, ZONE.sl, 'zsl')}
                {line(l.tp, COL.tp, true, 'TP')}
                {line(l.entry, COL.entry, false, 'ENTRY')}
                {line(l.sl, COL.sl, true, 'SL')}
                <div style={{ position: 'absolute', top: 8, left: 8, padding: '3px 9px', borderRadius: 4, background: '#f1f5f9', color: '#64748b', fontSize: 11, fontWeight: 800, letterSpacing: 0.4 }}>SETUP</div>
                <div style={{ position: 'absolute', top: 8, right: 66, padding: '4px 10px', borderRadius: 6, background: '#fff', border: `1px solid ${rrColor}`, color: rrColor, fontSize: 12, fontWeight: 800, fontFamily: 'monospace' }}>R:R 1:{rr.toFixed(2)}</div>
                <div style={{ position: 'absolute', bottom: 8, left: 8, fontSize: 11, color: '#94a3b8' }}>{t('No market data for')} {String(ticker).toUpperCase()} — {t('position drawn from your levels')}</div>
              </div>
            );
          })() : (
            <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'center', justifyContent: 'center', color: '#94a3b8', fontSize: 13, textAlign: 'center', padding: 20 }}>
              <span style={{ fontWeight: 700, color: '#64748b' }}>{t('No market data for')} {String(ticker).toUpperCase() || '—'}</span>
              <span style={{ fontSize: 12 }}>{t('Enter entry, SL and TP below to preview your position.')}</span>
            </div>
          )
        )}
      </div>
    </div>
  );
});

export default PositionChart;
